import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import { shallowEqual, useSelector } from 'react-redux';
import {
  toPercentage,
  toSignificant,
  toCurrency,
} from '@/utilities/formatting';
import { pathConstructor } from '@/hooks/useCompany/utils';

interface ReduxState {
  company: CompanyState;
  location: LocationState;
}

const formatter = (value: number | null, significance = 1) =>
  value !== null ? toSignificant(value, significance) : 'n/a';

const percentageFormatter = (value: number | null, significance = 1) =>
  value !== null ? toPercentage(value, significance) : 'n/a';

function selector(state: ReduxState) {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const formatting = state.company.formatting[companyId];
    const analysis = state.company.analysis[companyId];
    const company = state.company.summary[companyId];
    const {
      minPrice52w,
      maxPrice52w,
      beta5y,
      return30D,
      return90D,
      return1YrAbs,
      return3YrAbs,
      return5YrAbs,
      returnSinceIpoAbs,
    } = analysis;

    return {
      sharePrice: toCurrency(
        analysis.sharePrice,
        2,
        formatting.primaryTradingItemCurrencyISO
      ),
      minPrice52w: toCurrency(
        minPrice52w,
        2,
        formatting.primaryTradingItemCurrencyISO
      ),
      maxPrice52w: toCurrency(
        maxPrice52w,
        2,
        formatting.primaryTradingItemCurrencyISO
      ),
      companyName: company?.shortName,
      beta5y: formatter(beta5y, 2),
      return30D: percentageFormatter(return30D, 2),
      return90D: percentageFormatter(return90D, 2),
      return1YrAbs: percentageFormatter(return1YrAbs, 2),
      return3YrAbs: percentageFormatter(return3YrAbs, 2),
      return5YrAbs: percentageFormatter(return5YrAbs, 2),
      returnSinceIpoAbs: percentageFormatter(returnSinceIpoAbs, 2),
    };
  } catch (e) {
    /** means it's loading */
    return {
      companyName: 'company',
      minPrice52w: 'n/a',
      maxPrice52w: 'n/a',
      beta5y: 'n/a',
      return30D: 'n/a',
      return90D: 'n/a',
      return1YrAbs: 'n/a',
      return3YrAbs: 'n/a',
      return5YrAbs: 'n/a',
      returnSinceIpoAbs: 'n/a',
    };
  }
}

const PriceChartTable = () => {
  const selectedState = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const header = 'Historical stock prices';
  const caption = `Summary of all time highs, changes and price drops for ${selectedState.companyName}`;

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <ChartDataTable
        data-cy-id="overview-price-table"
        columnTextAlignments="l"
      >
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th colSpan={2}>{header}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Current Share Price</td>
            <td>{selectedState.sharePrice}</td>
          </tr>
          <tr>
            <td>52 Week High</td>
            <td>{selectedState.maxPrice52w}</td>
          </tr>
          <tr>
            <td>52 Week Low</td>
            <td>{selectedState.minPrice52w}</td>
          </tr>
          <tr>
            <td>Beta</td>
            <td>{selectedState.beta5y}</td>
          </tr>
          <tr>
            <td>1 Month Change</td>
            <td>{selectedState.return30D}</td>
          </tr>
          <tr>
            <td>3 Month Change</td>
            <td>{selectedState.return90D}</td>
          </tr>
          <tr>
            <td>1 Year Change</td>
            <td>{selectedState.return1YrAbs}</td>
          </tr>
          <tr>
            <td>3 Year Change</td>
            <td>{selectedState.return3YrAbs}</td>
          </tr>
          <tr>
            <td>5 Year Change</td>
            <td>{selectedState.return5YrAbs}</td>
          </tr>
          <tr>
            <td>Change since IPO</td>
            <td>{selectedState.returnSinceIpoAbs}</td>
          </tr>
        </tbody>
      </ChartDataTable>
    </>
  );
};

export default PriceChartTable;
